import React from 'react'
import { useSelector } from 'react-redux'
import { Button } from '@material-ui/core'
import MuiAlert from '@material-ui/lab/Alert'
import AlertTitle from '@material-ui/lab/AlertTitle'
import MigrationDialog from './MigrationDialog'

const MIGRATION_DATE = new Date('2026-10-05')
const MIGRATION_DEADLINE = new Date('2026-10-12')

/**
 * MigrationNotice — account-migration announcement and read-only banner.
 * Rendered near the top of the app (MainLayout and Login).
 *
 * Timeline:
 *  - before `MIGRATION_DATE`: shows a scheduled-migration notice announcing the
 *    start date and deadline;
 *  - between `MIGRATION_DATE` and `MIGRATION_DEADLINE`: migration window is
 *    open; the notice prompts users to migrate before the deadline and exposes
 *    the "Migrate account" CTA to authenticated users (`canMigrate`);
 *  - on/after `MIGRATION_DEADLINE`: the portal is forced into read-only mode
 *    and shows the post-deadline transition notice.
 *
 * Redux flags `app.readOnly` and `app.migrationEnabled` act as manual overrides
 * that can force read-only / migration mode before the scheduled dates.
 */
const formatMigrationDate = (date) =>
    date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })

const MigrationNotice = ({ canMigrate = false }) => {
    const app = useSelector((state) => state.app)
    const [dialogOpen, setDialogOpen] = React.useState(false)

    // Derive phase from the schedule; redux flags act as manual overrides.
    const now = new Date()
    const migrationOpen = now >= MIGRATION_DATE
    const pastDeadline = now >= MIGRATION_DEADLINE
    const readOnly = app.readOnly || pastDeadline
    const migrationEnabled = app.migrationEnabled || migrationOpen
    const migrationStart = formatMigrationDate(MIGRATION_DATE)
    const migrationDeadline = formatMigrationDate(MIGRATION_DEADLINE)

    const renderCta = () =>
        canMigrate ? (
            <Button color="inherit" size="small" variant="outlined" onClick={() => setDialogOpen(true)}>
                Migrate account
            </Button>
        ) : null

    return (
        <div style={{ margin: '8px 16px' }}>
            {
                pastDeadline ? (
                    <MuiAlert
                        severity="info"
                        variant="filled"
                        style={{ marginBottom: 8 }}
                        action={renderCta()}
                    >
                        <AlertTitle>Service transition notice</AlertTitle>
                        The Ericsson Service Portal has been succeeded by a new application, and the
                        account-migration window closed on {migrationDeadline}.
                        {
                            canMigrate
                                ? ' Please complete your migration now to restore full access in the new portal.'
                                : ' Please sign in and complete the account migration to restore full access in the new portal.'
                        }
                    </MuiAlert>
                ) : migrationOpen ? (
                    <MuiAlert
                        severity="info"
                        variant="filled"
                        style={{ marginBottom: 8 }}
                        action={renderCta()}
                    >
                        <AlertTitle>Account migration in progress</AlertTitle>
                        Account migration to the new Ericsson Service Portal is now open and must be
                        completed by {migrationDeadline}. After that date, this portal will operate in
                        read-only mode.
                        {
                            canMigrate ? ' Please migrate your account at your earliest convenience to retain uninterrupted access.'
                                : ' Please sign in and complete the account migration to retain uninterrupted access.'
                        }
                    </MuiAlert>
                ) : (
                    <MuiAlert severity="info" variant="filled" style={{ marginBottom: 8 }}>
                        <AlertTitle>Scheduled account migration</AlertTitle>
                        Account migration to the new Ericsson Service Portal will begin on
                        {' '}{migrationStart} and must be completed by {migrationDeadline}. After that
                        date, this portal will operate in read-only mode.
                    </MuiAlert>
                )
            }

            {
                readOnly ? (
                    <MuiAlert severity="warning" variant="filled">
                        <AlertTitle>Read-only mode</AlertTitle>
                        This portal is operating in read-only mode. Existing records remain available
                        for review, but creating or modifying tickets and account settings is no longer
                        supported. Please use the new Ericsson Service Portal for any changes.
                    </MuiAlert>
                ) : null
            }

            {
                migrationEnabled ? (
                    <MigrationDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
                ) : null
            }
        </div>
    )
}

export default MigrationNotice
